import { useEffect, useMemo, type ReactNode } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import type { ApolloClient } from '@apollo/client';
import { createApolloClient } from '@/lib/apollo/client';
import { getAccessToken, onAccessTokenChange } from '@/lib/auth/tokenStore';
import { SessionProvider } from '@/app/providers/SessionProvider';
import {
    ExternalNavigationProvider,
    type ExternalNavigator,
} from '@/app/providers/ExternalNavigationProvider';

interface AppProvidersProps {
    children: ReactNode;
    /** Injected by tests to supply a mocked client. Production passes nothing. */
    client?: ApolloClient;
    /** Injected by tests to supply a mocked external navigator. Production passes nothing. */
    navigator?: ExternalNavigator;
}

/**
 * Every cross-cutting provider the app needs, in one place.
 *
 * Tests render this with their own `client`/`navigator`, so a test exercises
 * the same provider tree as production rather than a hand-rolled
 * approximation.
 */
export function AppProviders({
    children,
    client,
    navigator,
}: Readonly<AppProvidersProps>) {
    // Stable identity: the client owns the cache, and is an effect dependency
    // below. A new client per render would discard the cache.
    const apolloClient = useMemo(
        () => client ?? createApolloClient(),
        [client]
    );

    // The cache holds per-user data (permissions, other users' details). When
    // the token goes away, or becomes a different one, none of it may survive
    // for the next person. A first sign-in (null -> token) clears nothing.
    useEffect(() => {
        let previous = getAccessToken();

        return onAccessTokenChange((next) => {
            if (previous !== null && next !== previous) {
                void apolloClient.clearStore().catch(() => undefined);
            }
            previous = next;
        });
    }, [apolloClient]);

    return (
        <ApolloProvider client={apolloClient}>
            <SessionProvider>
                <ExternalNavigationProvider navigator={navigator}>
                    {children}
                </ExternalNavigationProvider>
            </SessionProvider>
        </ApolloProvider>
    );
}
